import client from 'core/axios';
import handleError from 'core/axios/handleError';
import {
  handleJSONResponse,
  handleFileResponse,
} from 'core/axios/handleResponse';
import RequestMethod from 'core/axios/RequestMethod';

export const getContext = ({ version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/transcripts/context/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getTranscriptList = ({
  params = { limit: 20, offset: 0 },
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/transcripts/k12schools/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getTranscriptHistoryList = ({
  params = { limit: 20, offset: 0 },
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/transcripts/batch-transcripts/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const uploadBatchTranscript = ({ payload, version = 'v1' }) =>
  client
    .request({
      url: `${version}/transcripts/batch-transcripts/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getMissingStudentList = ({
  params = { limit: 20, offset: 0 },
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/transcripts/students/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createTranscript = ({ payload, version = 'v1' }) =>
  client
    .request({
      url: `${version}/transcripts/transcripts/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const replaceTranscriptFile = ({ id, payload, version = 'v1' }) =>
  client
    .request({
      url: `${version}/transcripts/transcripts/${id}/upload-file/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const retrieveTranscriptListing = ({ params, version = 'v1' }) =>
  client
    .request({
      url: `${version}/transcripts/record-requests/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const downloadTranscript = ({ uuid, version = 'v1' }) =>
  client
    .request({
      url: `${version}/transcripts/record-requests/${uuid}/print-documents/`,
      method: RequestMethod.POST,
      responseType: 'blob',
    })
    .then(handleFileResponse)
    .catch(handleError);

export const updateTranscriptSendStatus = ({ uuid, payload, version = 'v1' }) =>
  client
    .request({
      url: `${version}/transcripts/record-requests/${uuid}/update-send-status/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const printAllTranscripts = ({ params, version = 'v1' }) =>
  client
    .request({
      url: `${version}/transcripts/download-transcripts/`,
      method: RequestMethod.GET,
      params,
      responseType: 'blob',
    })
    .then(handleFileResponse)
    .catch(handleError);

export const getTranscriptCounts = ({ params, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/transcripts/record-requests/counts/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getUserDetails = ({ isAlum, userId, version = 'v1' }) => {
  const personType = isAlum ? 'alumni' : 'students';
  return client
    .request({
      url: `${version}/transcripts/students-and-alumni/${personType}/${userId}/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);
};

export const getLatestTranscript = ({ version = 'v1', params }) =>
  client
    .request({
      url: `${version}/transcripts/transcripts/latest/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createTranscriptRequest = ({ payload, params, version = 'v1' }) =>
  client
    .request({
      url: `${version}/transcripts/record-requests/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 *
 * @param {string} uuid -> uuid of the transcript request
 * @param {Object<{note, heSchool}>} payload -> data of the request
 * @returns
 */
export const updateTranscriptRequest = ({ uuid, payload, version = 'v1' }) =>
  client
    .request({
      url: `${version}/transcripts/record-requests/${uuid}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 *
 * @param {Object<{receiverName: string, receiverEmail: string, type: string}>} payload
 * @returns @createDestination Promise<Object<DestinationObject>>
 */
export const createDestination = ({ payload, version = 'v1' }) =>
  client
    .request({
      url: `${version}/transcripts/destinations/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createTranscriptRequestWithData = async ({
  transcriptRequest,
  payload,
  params,
  version = 'v1',
}) => {
  const { data } = await createDestination({ payload: transcriptRequest });
  return client
    .request({
      url: `${version}/transcripts/record-requests/`,
      method: RequestMethod.POST,
      data: {
        destinationPk: data?.id,
        ...payload,
      },
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);
};

export const uspsValidate = ({ address, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/transcripts/validate-address/`,
      method: RequestMethod.POST,
      data: address,
    })
    .then(handleJSONResponse)
    .catch(() => null);

export const getPredefinedRequests = ({ version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/transcripts/destinations/non-college/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const sendTranscriptRequest = ({ uuid, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/transcripts/record-requests/${uuid}/send/`,
      method: RequestMethod.POST,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const downloadTranscripts = ({ params, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/transcripts/download-transcripts/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const declineTranscriptRequest = ({
  uuid,
  payload,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/transcripts/record-requests/${uuid}/decline/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const searchStudents = ({ params = {}, version = 'v1' }) =>
  client
    .request({
      url: `${version}/transcripts/students-and-alumni/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateTranscriptDestination = ({
  destinationId,
  payload,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/transcripts/destinations/${destinationId}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStudentHash = ({ params, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/transcripts/students/student-hash/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const downloadCSV = ({ params, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/transcripts/students/students-csv/`,
      method: RequestMethod.GET,
      responseType: 'blob',
      params,
    })
    .then(handleFileResponse)
    .catch(handleError);

export const createRequestRecordInformation = ({
  payload,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/transcripts/record-request-information/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param {Object<[alumId or studentId]: number>} params
 * @returns getLatestImmunizationRecord -> Gets latest upload of the immunization record list of a student or alumni
 */
export const getLatestImmunizationRecord = ({ version = 'v1', params } = {}) =>
  client
    .request({
      url: `${version}/transcripts/immunization-records/latest/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param {Object<[alumId or studentId]: number, file: file_hash>} payload
 * @returns uploadImmunizationRecord -> Uploads immunization record behalf of student or alumni
 */
export const uploadImmunizationRecord = ({ version = 'v1', payload } = {}) =>
  client
    .request({
      url: `${version}/transcripts/immunization-records/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param {string} hashId given hashId in the record request named requestInformationId
 * @returns
 */
export const getRecordRequestInformation = ({
  version = 'v1',
  hashId,
  params,
} = {}) =>
  client
    .request({
      url: `${version}/transcripts/record-request-information/${hashId}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param {string} hashId given hashId in the record request named requestInformationId
 * @returns
 */
export const getSuggestedAlumniList = ({
  version = 'v1',
  hashId,
  params,
} = {}) =>
  client
    .request({
      url: `${version}/transcripts/record-request-information/${hashId}/suggested-alumni/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param {Object<...applied_vertical_filter_values, exclude_uuids, uuids>} params
 * @returns downloadCompletedRecordsCsv
 */
export const downloadCompletedRecordsCsv = ({ version = 'v1', params } = {}) =>
  client
    .request({
      url: `${version}/transcripts/record-requests/complete-records-csv/`,
      method: RequestMethod.POST,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param {Object<...applied_vertical_filter_values, exclude_uuids, uuids>} params
 * @returns downloadRequestedRecordsCsv
 */
export const downloadRequestedRecordsCsv = ({ version = 'v1', params } = {}) =>
  client
    .request({
      url: `${version}/transcripts/record-requests/requested-records-csv/`,
      method: RequestMethod.POST,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param {string} hashId -> hash_id of the record request
 * @param {Object<{denialReason: string}>} payload -> request data
 * @returns {async function} @denyRecordRequestVerification
 */
export const denyRecordRequestVerification = ({
  hashId,
  payload,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/transcripts/record-request-information/${hashId}/deny-verification/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param {string} hashId -> hash_id of the record request
 * @param {Object} payload -> request data
 * @returns {async function} @convertRequesterToAlumni
 */
export const convertRequesterToAlumni = ({
  hashId,
  payload,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/transcripts/record-request-information/${hashId}/convert-to-alum/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param {string} hashId -> hash_id of the record request
 * @param {Object<alumId: number>} payload -> request data
 * @returns {async function} @approveRecordRequestVerification
 */
export const approveRecordRequestVerification = ({
  hashId,
  payload,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/transcripts/record-request-information/${hashId}/approve/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param {string} hashId -> hash_id of the record request
 * @param {Object<RecordRequest>} payload -> propTypes.RecordRequest
 * @returns {async function} @updateRecordRequestInformation
 */
export const updateRecordRequestInformation = ({
  hashId,
  payload,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/transcripts/record-request-information/${hashId}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 *
 * @param {Object<{note: string, destinationPk: string, requestType: string, requesterSignature: string, ssnLastFourDigits: string}>} payload
 * @returns @createRecordRequest Promise<Object<RecordRequest>>
 */
export const createRecordRequest = ({ payload, version = 'v1' }) =>
  client
    .request({
      url: `${version}/transcripts/record-requests/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const downloadStudentTranscript = ({ version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/transcripts/download-transcript/`,
      method: RequestMethod.GET,
      responseType: 'blob',
    })
    .then(handleFileResponse)
    .catch(handleError);

/**
 * @param {string} uuid uuid of the record request created by student or alumni
 * @returns @sendRecordRequest
 */
export const sendRecordRequest = ({ uuid, payload, version = 'v1' }) =>
  client
    .request({
      url: `${version}/transcripts/record-requests/${uuid}/send/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getRecordCenterDistrictSettings = ({ params, version = 'v1' }) =>
  client
    .request({
      url: `${version}/transcripts/district-setting/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);
